import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  ArrowLeft, 
  FileSpreadsheet, 
  RefreshCw, 
  Download,
  Search,
  X,
  Check,
  PlusCircle,
  Merge
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import axios from 'axios';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { handleSupabaseUniqueConstraintError } from '@/lib/dbUtils';
import { StatusBadge } from '@/components/common/StatusBadge';
import PageContainer from '@/components/common/PageContainer';

// Interface for the product data coming from Ingram (adjust fields as needed)
interface IngramProduct { 
  mpn: string;
  mpn_custom?: string;
  vendor: string;
  id: string;
  name: string;
  description?: string; // Adicionando campo description opcional
  minimumQuantity: string;
  maximumQuantity: string;
  prices: {
    currency: string;
    amount: string;
    type: string;
    model: string;
    lowerLimit: number;
    cost_amount?: number;
  }[];
  costs: {
    currency: string;
    amount: string;
    type: string;
    model: string;
    lowerLimit: number;
  }[];
  billingPeriod: {
    type: string;
    duration: number;
  };
  subscriptionPeriod: {
    type: string;
    duration: number;
  };
  billingModel: string;
  dependsOn: any[];
  coterming: {
    required: string;
    type: string;
  };
  selected?: boolean;
  uniqueId: string;
  external_id?: string | null;
  isImported?: boolean;
}

interface PaginationInfo {
  offset: number;
  limit: number;
  total: number;
}

interface ApiResponse {
  data: IngramProduct[];
  pagination: PaginationInfo;
}

interface SubscriptionPlanFamily {
  id: string;
  name: string;
  description: string | null;
}

interface Vendor {
  id: string;
  name: string;
  status: string;
}

// Taxa de conversão USD para BRL
const USD_TO_BRL_RATE = 5.4866;

// URL da Edge Function para criar produto no HubSpot
const CREATE_HUBSPOT_PRODUCT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-product-hubspot`;

// URL da Edge Function para atualizar produto no HubSpot
const UPDATE_HUBSPOT_PRODUCT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-product-hubspot`;

// Função auxiliar para verificar se a moeda é USD
const isUSD = (currency: string): boolean => {
  const usdVariations = ['usd', 'us$', 'u$', 'dollar', 'dólar'];
  return usdVariations.includes(currency.toLowerCase()) || currency.toLowerCase().includes('usd');
};

// Helper function to map billing frequency values between formats
const normalizeBillingFrequency = (frequency: string, forDatabase = true): string => {
  if (forDatabase) {
    // Convert from UI/API format to database format (month, year)
    const frequencyMap: Record<string, string> = {
      'monthly': 'month',
      'annually': 'year',
      'yearly': 'year',
      'quarterly': 'quarter',
      'semiannually': 'semiannual',
      'one-time': 'one-time'
    };
    
    return frequencyMap[frequency] || frequency;
  } else {
    // Convert from database format to UI or HubSpot format (monthly, annually)
    const frequencyMap: Record<string, string> = {
      'month': 'monthly',
      'year': 'annually',
      'quarter': 'quarterly',
      'semiannual': 'semiannually',
      'one-time': 'one-time'
    };
    
    return frequencyMap[frequency] || frequency;
  }
};

// Helper function to format currency to exactly 3 characters
const formatCurrency = (currency: string): string => {
  // Extract just the currency code (usually first 3 characters)
  // Common formats: USD, BRL, EUR
  if (isUSD(currency)) {
    return 'USD';
  } else if (currency.toLowerCase().includes('brl') || currency.includes('R$')) {
    return 'BRL';
  } else if (currency.length >= 3) {
    return currency.substring(0, 3).toUpperCase();
  } else {
    // Pad if less than 3 characters
    return currency.toUpperCase().padEnd(3, ' ');
  }
};

// Função para formatar o SKU de acordo com a lógica especificada
const formatSkuForHubspot = (mpn: string, billingPeriodType: string, subscriptionPeriodType: string): string => {
  // Remover os dois pontos e qualquer caractere após eles
  let formattedSku = mpn.split(':')[0] + (mpn.split(':')[1] || '');
  
  // Adicionar sufixo P1 seguido pelo tipo de período
  let billingPeriodCode = '';
  let subscriptionPeriodCode = '';
  
  // Determinar código para billing_period_type
  if (billingPeriodType === 'month') {
    billingPeriodCode = 'M';
  } else if (billingPeriodType === 'year') {
    billingPeriodCode = 'Y';
  } else {
    billingPeriodCode = billingPeriodType.charAt(0).toUpperCase();
  }
  
  // Determinar código para subscription_period_type
  if (subscriptionPeriodType === 'month') {
    subscriptionPeriodCode = 'M';
  } else if (subscriptionPeriodType === 'year') {
    subscriptionPeriodCode = 'Y'; // 'Y' para anual, mantendo consistência com billingPeriodCode
  } else {
    subscriptionPeriodCode = subscriptionPeriodType.charAt(0).toUpperCase();
  }
  
  // Formatar conforme o padrão: MPNsemDoisPontosP1[BillingCode][SubscriptionCode]
  return `${formattedSku}P1${billingPeriodCode}${subscriptionPeriodCode}`;
};

// Função para criar produto no HubSpot
const createProductInHubspot = async (plan: any, priceRecords: any[]) => {
  try {
    console.log('🔍 Montando payload para criação no HubSpot:', plan);
    console.log('🔍 Description do plano:', {
      description: plan.description,
      type: typeof plan.description,
      length: plan.description?.length,
      trimmed: plan.description?.trim()
    });
    
    // Mapear service_type para hs_product_type
    const getHubSpotProductType = (serviceType: string) => {
      console.log('🔍 Mapeando service_type:', serviceType, 'tipo:', typeof serviceType);
      
      // Normalizar o valor (trim e lowercase)
      const normalizedType = serviceType?.trim()?.toLowerCase();
      console.log('🔍 Valor normalizado:', normalizedType);
      
      if (normalizedType === 'licensing') {
        console.log('✅ Mapeado para license');
        return 'license';
      }
      if (normalizedType === 'material') {
        console.log('✅ Mapeado para material');
        return 'material';
      }
      if (normalizedType === 'program') {
        console.log('✅ Mapeado para program');
        return 'program';
      }
      if (normalizedType === 'professional_service') {
        console.log('✅ Mapeado para service');
        return 'service';
      }
      console.log('✅ Mapeado para service (default)');
      return 'service'; // Default para todos os outros casos
    };

    // Mapear billing_period_type para recurringbillingfrequency
    const getRecurringBillingFrequency = (billingPeriodType: string) => {
      const frequencyMap: Record<string, string> = {
        'month': 'monthly',
        'year': 'annually',
        'quarter': 'quarterly',
        'semiannual': 'semiannually',
        'one-time': 'one-time'
      };
      return frequencyMap[billingPeriodType] || 'monthly';
    };

    // Usar o valor da coluna mpn_custom como SKU do HubSpot
    const formattedSku = plan.mpn_custom || plan.mpn || plan.sku;
    
    // Montar payload conforme especificação
    const payload: any = {
      name: plan.name
    };

    // Adicionar campos opcionais apenas se tiverem valores
    if (formattedSku && formattedSku.trim() !== '') {
      payload.hs_sku = formattedSku;
    }

    // Adicionar descrição se existir
    if (plan.description && plan.description.trim() !== '') {
      payload.description = plan.description;
    }

    // Buscar preços em BRL e USD
    const brlPrice = priceRecords.find(price => price.currency === 'BRL' && price.price_amount);
    const usdPrice = priceRecords.find(price => isUSD(price.currency) && price.price_amount);

    if (brlPrice && brlPrice.price_amount) {
      payload.hs_price_brl = brlPrice.price_amount.toString();
    }

    if (usdPrice && usdPrice.price_amount) {
      payload.hs_price_usd = usdPrice.price_amount.toString();
    }

    // Adicionar frequência de cobrança
    payload.recurringbillingfrequency = getRecurringBillingFrequency(plan.billing_period_type);

    // Adicionar tipo de produto
    console.log('🔍 DEBUG service_type antes do mapeamento:', {
      value: plan.service_type,
      type: typeof plan.service_type,
      length: plan.service_type?.length
    });
    payload.hs_product_type = getHubSpotProductType(plan.service_type || 'licensing');

    // Adicionar custo dos produtos vendidos (usar o primeiro custo disponível)
    const firstPriceWithCost = priceRecords.find(price => price.cost_amount);
    if (firstPriceWithCost && firstPriceWithCost.cost_amount) {
      payload.hs_cost_of_goods_sold = firstPriceWithCost.cost_amount.toString();
    }

    if (!import.meta.env.PROD) console.log('📤 Payload sendo enviado para edge function:', JSON.stringify(payload, null, 2));

    // Fazer a requisição para a edge function
    const response = await axios.post(
      CREATE_HUBSPOT_PRODUCT_URL,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        }
      }
    );

    if (!import.meta.env.PROD) console.log('Resposta completa do HubSpot:', response.data);
    
    // Extrair o ID do produto considerando a nova estrutura
    const hubspotProductId = response.data?.data?.id;
    if (!import.meta.env.PROD) console.log('ID do produto HubSpot:', hubspotProductId);
    
    if (response.data?.data?.properties) {
      if (!import.meta.env.PROD) console.log('Propriedades do produto HubSpot:', response.data.data.properties);
    }
    
    return {
      success: true,
      data: response.data.data, // Retornar data.data que contém o objeto do produto
      message: response.data.message || 'Produto criado com sucesso no HubSpot'
    };
  } catch (error: any) {
    console.error('Erro ao criar produto no HubSpot:', error.response?.data || error.message);
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Erro desconhecido ao criar produto no HubSpot'
    };
  }
};

// Define the type for the import progress state explicitly
type ImportProgressStatus = 'pending' | 'success' | 'error';
type ImportProgressItem = {
  name: string;
  sku: string;
  status: ImportProgressStatus;
  message?: string;
};

// Define the type for the data structure after initial mapping
interface MappedPlanData {
  name: string;
  description: string;
  vendor: string; // Ensure vendor is here
  billing_frequency: string;
  is_active: boolean;
  sku: string;
  // No vendor_id here, it's fetched separately
  vendor_subscription_name: string;
  vendor_part_number: string;
  minimum_quantity: number;
  maximum_quantity: number | undefined;
  billing_model: string;
  pricing_model: string;
  billing_period_type: string;
  billing_period_duration: number;
  subscription_period_type: string;
  subscription_period_duration: number;
  family_id: string | null;
  unit_measure: string;
  service_type: string;
  imported: boolean;
  created_by: string;
  updated_by: string;
  prices: {
    currency: string;
    price_amount: number;
    cost_amount: number;
    margin: number;
  }[];
  external_id: string | null;
  ingram_product_id: string;
  mpn_custom?: string;
}

// Helper function to map billing model values to valid schema values
const normalizeBillingModel = (billingModel: string): string => {
  // Map various billing model formats to the two accepted values
  const modelMap: Record<string, string> = {
    'chargeBeforeBillingPeriod': 'on creation',
    'chargeAfterBillingPeriod': 'on activation',
    'on_creation': 'on creation',
    'on_activation': 'on activation',
    'before': 'on creation',
    'after': 'on activation',
    'prepaid': 'on creation',
    'postpaid': 'on activation'
  };
  
  return modelMap[billingModel] || 'on creation'; // Default to 'on creation'
};

// Helper function to map vendor codes to vendor names or search patterns
const getVendorSearchName = (vendorCode: string): string => {
  const vendorMap: Record<string, string> = {
    'VA-888-104': 'Microsoft',
    'Adobe': 'Adobe',
    'Google': 'Google',
    'VA-888-101': 'Oracle',
    'VA-888-102': 'SAP',
    'VA-888-103': 'IBM'
  };
  
  return vendorMap[vendorCode] || vendorCode;
};

const ImportSubscriptionsPlans = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [products, setProducts] = useState<IngramProduct[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    offset: 0,
    limit: 20,
    total: 0
  });
  
  // Filtros
  const [vendorFilter, setVendorFilter] = useState<string>('all'); // Changed to 'all'
  const [nameFilter, setNameFilter] = useState<string>('');
  const [mpnFilter, setMpnFilter] = useState<string>('');
  const [importedFilter, setImportedFilter] = useState<string>('all'); // 'all', 'imported', 'not_imported'
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(20);

  // Move all useState declarations from outside the component to here
  // Adicionar novos estados para o modal de progresso
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgressItem[]>([]);
  const [importComplete, setImportComplete] = useState(false);
  const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0);
  // Adicionar estado para controlar a exibição dos detalhes de erro
  const [selectedProgressItem, setSelectedProgressItem] = useState<number | null>(null);

  // URL da Edge Function
  const EDGE_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-ingram-products`;

  const [planFamilies, setPlanFamilies] = useState<SubscriptionPlanFamily[]>([]);
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<IngramProduct[]>([]);
  const [showNewFamilyForm, setShowNewFamilyForm] = useState(false);
  const [newFamilyName, setNewFamilyName] = useState('');
  const [newFamilyDescription, setNewFamilyDescription] = useState('');
  const [creatingFamily, setCreatingFamily] = useState(false);
  const [loadingFamilies, setLoadingFamilies] = useState(false);

  // Vendors state
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loadingVendors, setLoadingVendors] = useState(false);

  // Estados para o modal de progresso de importação
  const [importedCount, setImportedCount] = useState(0);
  const [totalToImport, setTotalToImport] = useState(0);
  const [currentProductName, setCurrentProductName] = useState('');
  const [showProgressDialog, setShowProgressDialog] = useState(false);

  // Add a state for unit_measure selection
  const [selectedUnitMeasure, setSelectedUnitMeasure] = useState<string>('user');

  // State for page input
  const [pageInput, setPageInput] = useState<string>('');

  // Estados para o modal de mesclagem
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [selectedProductForMerge, setSelectedProductForMerge] = useState<IngramProduct | null>(null);
  const [hubspotProductId, setHubspotProductId] = useState('');
  const [isMerging, setIsMerging] = useState(false);
  const [selectedMergeFamily, setSelectedMergeFamily] = useState<string | null>(null);

  // Função para buscar produtos da Ingram Micro através da view formatada
  const fetchProducts = async (page = 1) => {
    setIsLoading(true);
    try {
      const offset = (page - 1) * itemsPerPage;
      
      // Construir a query base usando apenas os campos necessários da view
      let query = supabase
        .from('subscription_plans_ingram_formated')
        .select(`
          id,
          mpn,
          vendor,
          vendor_id,
          vendor_name,
          product_id,
          mpn_custom,
          name,
          description,
          minimum_quantity,
          maximum_quantity,
          billing_period_type,
          billing_period_duration,
          subscription_period_type,
          subscription_period_duration,
          price_type,
          price_amount,
          price_currency,
          cost_type,
          cost_amount,
          cost_currency,
          external_id,
          created_at
        `, { count: 'exact' });
      
      // Aplicar filtros
      if (vendorFilter && vendorFilter !== 'all') {
        query = query.eq('vendor_id', vendorFilter);
      }
      
      if (nameFilter) {
        query = query.ilike('name', `%${nameFilter}%`);
      }
      
      if (mpnFilter) {
        query = query.ilike('mpn', `%${mpnFilter}%`);
      }
      
      // Aplicar filtro de importação
      if (importedFilter === 'imported') {
        query = query.not('external_id', 'is', null);
      } else if (importedFilter === 'not_imported') {
        query = query.is('external_id', null);
      }
      
      // Aplicar paginação
      const { data, error, count } = await query
        .range(offset, offset + itemsPerPage - 1)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
              // Transformar os dados do formato do Supabase para o formato esperado pela aplicação
        if (!import.meta.env.PROD) console.log('Dados brutos da view antes da transformação:', data);
        const transformedProducts = data.map((item, idx) => {
          if (!import.meta.env.PROD) console.log(`Item ${idx} description:`, item.description);
        // Criar estrutura de preços e custos baseada nos novos campos da view
        const prices = [{
          currency: item.price_currency,
          amount: item.price_amount?.toString() || '0',
          type: item.price_type || 'recurring',
          model: 'flat', // Default value since price_model doesn't exist
          lowerLimit: 1
        }];
        
        const costs = [{
          currency: item.cost_currency,
          amount: item.cost_amount?.toString() || '0',
          type: item.cost_type || 'recurring',
          model: 'flat', // Default value since cost_model doesn't exist
          lowerLimit: 1
        }];

        // Criar estrutura de períodos baseada nos novos campos
        const billingPeriod = {
          type: item.billing_period_type || 'month',
          duration: parseInt(item.billing_period_duration) || 1
        };
        
        const subscriptionPeriod = {
          type: item.subscription_period_type || 'month',
          duration: parseInt(item.subscription_period_duration) || 1
        };

        // Verificar se o produto já foi importado baseado no external_id
        const isImported = Boolean(item.external_id);

        // Criar objeto no formato esperado
        return {
          mpn: item.mpn,
          mpn_custom: item.mpn_custom,
          vendor: item.vendor_name || item.vendor, // Usar vendor_name se disponível
          id: item.product_id || '',
          name: item.name,
          description: item.description, // Usar o valor direto da view
          minimumQuantity: item.minimum_quantity?.toString() || '1',
          maximumQuantity: item.maximum_quantity?.toString() || '9999',
          prices,
          costs,
          billingPeriod,
          subscriptionPeriod,
          billingModel: 'on creation', // Valor padrão
          dependsOn: [],
          coterming: { required: 'false', type: '' },
          selected: false,
          uniqueId: `${item.id}-${idx}`, // Usar o ID da view para melhor identificação
          external_id: item.external_id,
          isImported
        } as IngramProduct;
      });
      
      setProducts(transformedProducts);
      setPagination({
        offset,
        limit: itemsPerPage,
        total: count || 0
      });
      setCurrentPage(page);
      

    } catch (error: any) {
      console.error('Erro ao buscar produtos:', error);
      toast.error(`Falha ao carregar produtos: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch plan families
  const fetchPlanFamilies = async () => {
    setLoadingFamilies(true);
    try {
      const { data, error } = await supabase
        .from('subscription_plan_families')
        .select('*')
        .order('name');

      if (error) throw error;
      setPlanFamilies(data || []);
    } catch (error: any) {
      console.error('Error fetching plan families:', error);
      toast.error(`Failed to load plan families: ${error.message}`);
    } finally {
      setLoadingFamilies(false);
    }
  };

  // Fetch vendors
  const fetchVendors = async () => {
    setLoadingVendors(true);
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('id, name, status')
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      setVendors(data || []);
    } catch (error: any) {
      console.error('Error fetching vendors:', error);
      toast.error(`Failed to load vendors: ${error.message}`);
    } finally {
      setLoadingVendors(false);
    }
  };

  // Carregar produtos ao montar o componente
  useEffect(() => {
    fetchProducts(1);
    fetchPlanFamilies();
    fetchVendors();
  }, []);

  // Atualizar no início das funções de processamento para incluir o ID do Supabase
  const processSubscriptionPlan = (product, family_id = null, unitMeasure = 'user') => {
    console.log('Processando plano, description recebido:', product.description);
    // Obter o preço e custo (primeiro item do array ou 0)
    const price = product.prices.length > 0 ? parseFloat(product.prices[0].amount) : 0;
    
    // Calcular o custo como 21,054% do preço se não houver custo fornecido pela API
    let cost = product.costs.length > 0 ? parseFloat(product.costs[0].amount) : 0;
    
    // Se o custo for 0 ou não existir, calcular como 21,054% do preço
    if (cost === 0 && price > 0) {
      cost = price * 0.21054;
    }
    
    // Usar mpn_custom como SKU, com fallback para mpn se não existir
    const formattedSku = product.mpn_custom || product.mpn;
    
    // Normalizar billing_frequency para o formato esperado pelo banco de dados
    // Para o banco de dados, queremos 'month' ao invés de 'monthly'
    const normalizedFrequency = normalizeBillingFrequency(product.billingPeriod.type, true);
    
    // Normalizar billing_model para valores aceitos pelo schema
    const normalizedBillingModel = normalizeBillingModel(product.billingModel || 'on creation');
    
    // Criar a estrutura base para o plano
    console.log('Valores antes de criar o plano:', {
      name: product.name,
      description: product.description
    });
    
    return {
      name: product.name,
      description: product.description, // Removido o fallback para string vazia aqui
      billing_frequency: normalizedFrequency,
      is_active: true,
      sku: formattedSku,
      vendor_external_id: product.vendor,
      vendor_part_number: formattedSku,
      vendor_subscription_name: product.name,
      minimum_quantity: parseInt(product.minimumQuantity) || 1,
      maximum_quantity: parseInt(product.maximumQuantity) || 9999,
      billing_model: normalizedBillingModel,
      pricing_model: 'per unit',
      billing_period_type: product.billingPeriod.type === 'month' || product.billingPeriod.type === 'year' 
        ? product.billingPeriod.type 
        : 'month',
      billing_period_duration: product.billingPeriod.duration,
      subscription_period_type: product.subscriptionPeriod.type === 'month' || product.subscriptionPeriod.type === 'year' 
        ? product.subscriptionPeriod.type 
        : 'month',
      subscription_period_duration: product.subscriptionPeriod.duration,
      family_id: family_id,
      unit_measure: unitMeasure,
      service_type: 'licensing',
      imported: true,
      
      // Campos de auditoria
      created_by: 'import_process',
      updated_by: 'import_process',
      
      // Adicionar referência à tabela original
      original_id: product.id,
      
      // Dados para external_ids - USAR O MESMO VALOR DO SKU
      external_id: formattedSku,
      system_name: 'ingram_micro',
      entity_name: 'subscription_plans',
      
      // Dados para subscription_plan_prices
      prices: product.prices.map(price => {
        const priceAmount = parseFloat(price.amount);
        const costAmount = product.costs.length > 0 
          ? parseFloat(product.costs[0].amount) 
          : priceAmount * 0.21054;
        
        const margin = priceAmount > 0 
          ? ((priceAmount - costAmount) / priceAmount) * 100 
          : 0;
          
        return {
          currency: formatCurrency(price.currency),
          price_amount: priceAmount,
          cost_amount: costAmount,
          margin: parseFloat(margin.toFixed(2)),
          lower_limit: price.lowerLimit || 1
        };
      }),
      costs: product.costs
    };
  };

  // Função para importar produtos selecionados
  const handleImportProducts = async () => {
    const selectedProducts = products.filter(product => product.selected);
    
    if (selectedProducts.length === 0) {
      toast.error('Selecione pelo menos um produto para importar');
      return;
    }
    
    setIsImporting(true);
    
    try {
      // Preparar dados usando a função processSubscriptionPlan
      const subscriptionPlansData = selectedProducts.map(product => 
        processSubscriptionPlan(product, null, selectedUnitMeasure)
      );
      
      // Processar produtos um por um para atualização de status
      setImportProgress([]);
      setImportedCount(0);
      setTotalToImport(selectedProducts.length);
      setShowProgressDialog(true);
      
      let successCount = 0;
      let errorCount = 0;
      
      for (let i = 0; i < subscriptionPlansData.length; i++) {
        const planData = subscriptionPlansData[i];
        setCurrentProductName(`${i + 1}/${selectedProducts.length}: ${planData.name}`);
        
        try {
          // Como agora usamos a view formatada, o campo external_id já indica se foi importado
          // Se o produto já foi importado, não deveria estar na lista de selecionados
          // mas vamos manter esta verificação como segurança adicional
          if (planData.external_id) {
            console.log(`Plano ${planData.name} já foi importado anteriormente. Pulando importação.`);
            
            setImportProgress(prev => [...prev, { 
              name: planData.name, 
              sku: planData.sku || '',
              status: 'error',
              message: 'Produto já existe no sistema'
            }]);
            errorCount++;
            continue;
          }
          
          // 1. Inserir na tabela subscription_plans
          const { data: planInsertData, error: planInsertError } = await supabase
            .from('subscription_plans')
            .insert({
              name: planData.name,
              description: planData.description,
              billing_frequency: planData.billing_frequency,
              is_active: planData.is_active,
              sku: planData.sku,
              vendor_id: planData.vendor_external_id, // Usar o ID externo do fornecedor
              vendor_subscription_name: planData.vendor_subscription_name,
              vendor_part_number: planData.vendor_part_number,
              minimum_quantity: planData.minimum_quantity,
              maximum_quantity: planData.maximum_quantity,
              billing_model: planData.billing_model,
              pricing_model: planData.pricing_model,
              billing_period_type: planData.billing_period_type,
              billing_period_duration: planData.billing_period_duration,
              subscription_period_type: planData.subscription_period_type,
              subscription_period_duration: planData.subscription_period_duration,
              family_id: planData.family_id,
              unit_measure: planData.unit_measure,
              service_type: 'licensing',
              imported: true,
              created_by: planData.created_by,
              updated_by: planData.updated_by
            })
            .select('id')
            .single();

          if (planInsertError) throw planInsertError;
          
          // Se o insert for bem-sucedido, pegue o ID do plano inserido
          const plan_id = planInsertData.id;
          
          // 2. Inserir na tabela external_ids
          const { error: externalIdError } = await supabase
            .from('external_ids')
            .insert({
              external_id: planData.external_id,
              entity_id: plan_id,
              system_name: planData.system_name,
              entity_name: planData.entity_name
            });

          if (externalIdError) throw externalIdError;
          
          // 3. Criar produtos no HubSpot se necessário
          // Encontrar preços em BRL e USD, se existirem
          const brlPrice = planData.prices.find(price => 
            price.currency === 'BRL' || price.currency === 'R$' || price.currency.includes('BRL')
          );
          
          const usdPrice = planData.prices.find(price => 
            isUSD(price.currency)
          );
          
          // Se houver preços em BRL ou USD, criar o produto no HubSpot
          if (brlPrice || usdPrice) {
            const hubspotResult = await createProductInHubspot(planData, planData.prices);
            
            // Se a criação no HubSpot for bem-sucedida, armazenar o ID retornado
            if (hubspotResult.success && hubspotResult.data && hubspotResult.data.id) {
              console.log(`Salvando ID do HubSpot ${hubspotResult.data.id} para o plano ${plan_id}`);
              const { error: hubspotIdError } = await supabase
                .from('external_ids')
                .insert({
                  entity_name: 'subscription_plans',
                  entity_id: plan_id,
                  system_name: 'hubspot',
                  external_id: hubspotResult.data.id
                });
                
              if (hubspotIdError) {
                console.error('Erro ao salvar ID do HubSpot:', hubspotIdError);
                toast.warning(`Aviso: Produto importado, mas houve um erro ao salvar o ID do HubSpot: ${hubspotIdError.message}`);
              } else {
                console.log(`ID do HubSpot ${hubspotResult.data.id} salvo com sucesso para o plano ${planData.name}`);
              }
            } else {
              console.warn('ID do HubSpot não encontrado na resposta:', hubspotResult);
            }
          }
          
          // 4. Inserir preços na tabela subscription_plan_prices
          for (const price of planData.prices) {
            const { error: priceError } = await supabase
              .from('subscription_plan_prices')
              .insert({
                subscription_plan_id: plan_id,
                currency: formatCurrency(price.currency),
                price_amount: price.price_amount,
                cost_amount: price.cost_amount,
                margin: price.margin,
                lower_limit: price.lower_limit,
                created_by: 'import_process',
                updated_by: 'import_process'
              });

            if (priceError) throw priceError;
          }
          
          // Atualizar progresso com sucesso
          setImportProgress(prev => [...prev, { 
            name: planData.name, 
            sku: planData.sku || '',
            status: 'success',
            message: 'Plano importado com sucesso'
          }]);
          successCount++;
        } catch (error) {
          console.error(`Erro ao importar plano ${planData.name}:`, error);
          
          // Atualizar progresso com erro
          setImportProgress(prev => [...prev, { 
            name: planData.name, 
            sku: planData.sku || '',
            status: 'error',
            message: `Erro: ${error.message || 'Falha ao importar'}`
          }]);
          errorCount++;
        } finally {
          // Incrementar contador de importados
          setImportedCount(prev => prev + 1);
        }
      }
      
      // Exibir mensagem de conclusão
      if (errorCount === 0) {
        toast.success(`${successCount} produtos importados com sucesso!`);
      } else {
        toast.warning(`Importação concluída: ${successCount} sucesso, ${errorCount} falhas.`);
      }
      
      // Desmarcar produtos selecionados
      setProducts(products.map(p => ({ ...p, selected: false })));
    } catch (error) {
      console.error('Erro ao importar produtos:', error);
      toast.error(`Falha na importação: ${error.message}`);
      setShowProgressDialog(false);
    } finally {
      setIsImporting(false);
    }
  };

  // Função para selecionar/deselecionar todos os produtos (apenas os não importados)
  const handleSelectAll = (checked: boolean) => {
    setProducts(products.map(product => ({
      ...product,
      selected: product.isImported ? false : checked
    })));
    
    // Atualizar a lista de produtos selecionados (apenas os não importados)
    if (checked) {
      setSelectedProducts(products.filter(p => !p.isImported));
    } else {
      setSelectedProducts([]);
    }
  };

  // Função para selecionar/deselecionar um produto (apenas se não foi importado)
  const handleSelectProduct = (id: string, checked: boolean) => {
    console.log(`Selecting product with ID: ${id}, checked: ${checked}`);
    
    // Encontrar o produto para verificar se já foi importado
    const product = products.find(p => p.uniqueId === id);
    if (product?.isImported) {
      console.log('Produto já importado, não pode ser selecionado');
      return;
    }
    
    // Atualizar o estado dos produtos
    const updatedProducts = products.map(product => 
      product.uniqueId === id ? { ...product, selected: checked } : product
    );
    
    setProducts(updatedProducts);
    
    // Atualizar a lista de produtos selecionados (apenas os não importados)
    setSelectedProducts(updatedProducts.filter(p => p.selected && !p.isImported));
  };

  // Função para alternar a seleção de um produto ao clicar na linha (apenas se não foi importado)
  const handleRowClick = (id: string) => {
    console.log(`Row click for product with ID: ${id}`);
    
    // Encontrar o produto para verificar se já foi importado
    const product = products.find(p => p.uniqueId === id);
    if (product?.isImported) {
      console.log('Produto já importado, não pode ser selecionado');
      return;
    }
    
    // Alternar a seleção do produto
    const updatedProducts = products.map(product => 
      product.uniqueId === id ? { ...product, selected: !product.selected } : product
    );
    
    setProducts(updatedProducts);
    
    // Atualizar a lista de produtos selecionados (apenas os não importados)
    setSelectedProducts(updatedProducts.filter(p => p.selected && !p.isImported));
    
    console.log('Updated products after row click:', updatedProducts.filter(p => p.selected && !p.isImported).length);
  };

  // Função para selecionar múltiplos produtos com Shift+Click (apenas os não importados)
  const handleShiftClick = (index: number) => {
    console.log(`Shift click at index: ${index}`);
    
    // Verificar se o produto atual pode ser selecionado
    if (products[index]?.isImported) {
      console.log('Produto já importado, não pode ser selecionado');
      return;
    }
    
    // Se não houver seleção anterior, seleciona apenas o item atual
    if (lastSelectedIndex === -1) {
      handleRowClick(products[index].uniqueId);
      setLastSelectedIndex(index);
      return;
    }

    // Determinar o intervalo de seleção
    const start = Math.min(lastSelectedIndex, index);
    const end = Math.max(lastSelectedIndex, index);
    console.log(`Selecting range from ${start} to ${end}`);

    // Atualizar o estado dos produtos no intervalo (apenas os não importados)
    const updatedProducts = products.map((product, i) => {
      if (i >= start && i <= end && !product.isImported) {
        return { ...product, selected: true };
      }
      return product;
    });
    
    setProducts(updatedProducts);
    
    // Atualizar a lista de produtos selecionados (apenas os não importados)
    setSelectedProducts(updatedProducts.filter(p => p.selected && !p.isImported));
    
    console.log('Updated products after shift click:', updatedProducts.filter(p => p.selected && !p.isImported).length);

    // Atualizar o último índice selecionado
    setLastSelectedIndex(index);
  };

  // Estado para rastrear o último índice selecionado (para seleção com Shift)
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(-1);

  // Função para aplicar filtros
  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchProducts(1);
  };

  // Função para limpar filtros
  const handleClearFilters = () => {
    setVendorFilter('all'); // Reset to 'all'
    setNameFilter('');
    setMpnFilter('');
    setImportedFilter('all');
    setCurrentPage(1);
    fetchProducts(1);
  };

  // Função para alterar o número de itens por página
  const handleItemsPerPageChange = (value: string) => {
    const newSize = parseInt(value);
    setItemsPerPage(newSize);
    setCurrentPage(1);
    // Atualizamos a paginação e buscamos os produtos novamente
    setPagination(prev => ({
      ...prev,
      limit: newSize
    }));
    fetchProducts(1);
  };

  // Função para navegar entre páginas
  const handlePageChange = (page: number) => {
    if (page < 1 || page > Math.ceil(pagination.total / pagination.limit)) {
      return;
    }
    fetchProducts(page);
  };

  // Função para ir para uma página específica digitada
  const handleGoToPage = () => {
    const page = parseInt(pageInput);
    const totalPages = Math.ceil(pagination.total / pagination.limit);
    
    if (isNaN(page) || page < 1 || page > totalPages) {
      toast.error(`Por favor, digite um número entre 1 e ${totalPages}`);
      return;
    }
    
    handlePageChange(page);
    setPageInput('');
  };

  // Função para lidar com Enter no input da página
  const handlePageInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  // Função para renderizar a paginação
  const renderPagination = () => {
    const totalPages = Math.ceil(pagination.total / pagination.limit);
    
    if (totalPages <= 1) {
      return null;
    }
    
    return (
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Página {currentPage} de {totalPages}</span>
          <span>({pagination.total} itens no total)</span>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Page input section */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Ir para página:</span>
            <Input
              type="number"
              min="1"
              max={totalPages}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              onKeyPress={handlePageInputKeyPress}
              className="w-16 h-8 text-center"
              placeholder={currentPage.toString()}
            />
            <Button 
              size="sm" 
              variant="outline" 
              onClick={handleGoToPage}
              disabled={!pageInput}
            >
              Ir
            </Button>
          </div>

          {/* Standard pagination */}
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => handlePageChange(currentPage - 1)}
                  className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
              
              {currentPage > 2 && (
                <PaginationItem>
                  <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
                </PaginationItem>
              )}
              
              {currentPage > 3 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}
              
              {currentPage > 1 && (
                <PaginationItem>
                  <PaginationLink onClick={() => handlePageChange(currentPage - 1)}>
                    {currentPage - 1}
                  </PaginationLink>
                </PaginationItem>
              )}
              
              <PaginationItem>
                <PaginationLink isActive>{currentPage}</PaginationLink>
              </PaginationItem>
              
              {currentPage < totalPages && (
                <PaginationItem>
                  <PaginationLink onClick={() => handlePageChange(currentPage + 1)}>
                    {currentPage + 1}
                  </PaginationLink>
                </PaginationItem>
              )}
              
              {currentPage < totalPages - 2 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}
              
              {currentPage < totalPages - 1 && (
                <PaginationItem>
                  <PaginationLink onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                  </PaginationLink>
                </PaginationItem>
              )}
              
              <PaginationItem>
                <PaginationNext 
                  onClick={() => handlePageChange(currentPage + 1)}
                  className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
    );
  };

  // Função para obter o preço formatado
  const getFormattedPrice = (product: IngramProduct) => {
    if (product.prices.length === 0) return 'N/A';
    const price = parseFloat(product.prices[0].amount);
    
    // Formatar o preço no padrão brasileiro (R$ 0.000,00)
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  // Função para obter o período formatado
  const getFormattedPeriod = (product: IngramProduct) => {
    // Determinar o período de cobrança
    let period = '';
    
    if (product.billingPeriod.type === 'monthly' || product.billingPeriod.type === 'month') {
      period = 'Mês';
    } else if (product.billingPeriod.type === 'yearly' || product.billingPeriod.type === 'annually' || product.billingPeriod.type === 'year') {
      period = 'Ano';
    } else if (product.billingPeriod.type === 'quarterly') {
      period = 'Trimestre';
    } else if (product.billingPeriod.type === 'semiannually') {
      period = 'Semestre';
    } else if (product.billingPeriod.type === 'one-time') {
      period = 'Único';
    } else {
      period = product.billingPeriod.type;
    }
    
    return period;
  };

  // Função para formatar tipo de período em português
  const formatPeriodType = (periodType: string) => {
    if (periodType === 'monthly' || periodType === 'month') {
      return 'Mensal';
    } else if (periodType === 'yearly' || periodType === 'annually' || periodType === 'year') {
      return 'Anual';
    } else if (periodType === 'quarterly') {
      return 'Trimestral';
    } else if (periodType === 'semiannually') {
      return 'Semestral';
    } else if (periodType === 'one-time') {
      return 'Único';
    } else {
      return periodType || '-';
    }
  };

  // Função para formatar período com duração - exemplo: "Mensal (1)"
  const formatPeriodWithDuration = (periodType: string, duration: number) => {
    const formattedType = formatPeriodType(periodType);
    return `${formattedType} (${duration || 1})`;
  };

  // Function to handle import button click
  const handleImportClick = () => {
    const availableProducts = selectedProducts.filter(p => !p.isImported);
    if (availableProducts.length === 0) {
      toast.error('Selecione pelo menos um produto não importado para importar');
      return;
    }
    
    setShowFamilyModal(true);
  };

  // Função para importar produtos com a família selecionada
  const handleImportWithFamily = async () => {
    if (!selectedFamily) {
      toast.error('Por favor, selecione uma família de planos');
      return;
    }

    const productsToImport = products.filter(p => p.selected && !p.isImported);
    if (productsToImport.length === 0) {
       toast.error('Nenhum produto disponível para importar.');
       return;
    }

    setShowFamilyModal(false);
    setIsImporting(true);
    setShowProgressModal(true);
    setImportComplete(false);
    // Initialize progress state
    setImportProgress(productsToImport.map(p => ({
        name: p.name,
        sku: p.mpn, // Use mpn as initial identifier
        status: 'pending',
        message: ''
    })));
    setCurrentProcessingIndex(-1);

    try {
      // Map selected products to the MappedPlanData structure
      // This structure holds necessary info before DB lookups/inserts
      const subscriptionPlansData: MappedPlanData[] = productsToImport.map(product => {
          return {
             name: product.name,
             description: product.description || '', // Usar o valor direto da view ou string vazia
             vendor: product.vendor, // Include vendor name for lookup
             billing_frequency: normalizeBillingFrequency(product.billingPeriod?.type || 'month'),
             is_active: true,
             sku: product.mpn_custom || product.mpn,
             vendor_subscription_name: product.name,
             vendor_part_number: product.mpn,
             minimum_quantity: parseInt(product.minimumQuantity) || 1,
             maximum_quantity: parseInt(product.maximumQuantity) || undefined,
             billing_model: normalizeBillingModel(product.billingModel || 'on creation'),
             pricing_model: 'per unit',
             billing_period_type: product.billingPeriod?.type || 'month',
             billing_period_duration: product.billingPeriod?.duration || 1,
             subscription_period_type: product.subscriptionPeriod?.type || 'month',
             subscription_period_duration: product.subscriptionPeriod?.duration || 1,
             family_id: selectedFamily, // Use selectedFamily state
             unit_measure: 'user',
             service_type: 'licensing',
             imported: true,
             created_by: 'import_process',
             updated_by: 'import_process',
             prices: product.prices.map(price => {
                const priceAmount = parseFloat(price.amount);
                const costAmount = product.costs.length > 0
                  ? parseFloat(product.costs[0].amount)
                  : priceAmount * 0.21054;
                const margin = priceAmount > 0
                  ? ((priceAmount - costAmount) / priceAmount) * 100
                  : 0;
                return {
                  currency: formatCurrency(price.currency),
                  price_amount: priceAmount,
                  cost_amount: costAmount,
                  margin: parseFloat(margin.toFixed(2))
                };
              }),
             external_id: product.external_id, // Use external_id from view to check if already imported
             ingram_product_id: product.id, // Ingram product ID for external_ids table
             mpn_custom: product.mpn_custom // MPN custom value for external_id
          };
      });

      console.log('Dados mapeados para importar:', subscriptionPlansData);

      let convertedPricesCount = 0;
      let duplicatePlansCount = 0;
      let processedCount = 0;
      let errorCount = 0;

      // Find Vendor ID *once* before the loop using the mapped data
      let vendorId: string | null = null;
      if (subscriptionPlansData.length > 0 && subscriptionPlansData[0].vendor) {
           const vendorCode = subscriptionPlansData[0].vendor;
           const vendorName = getVendorSearchName(vendorCode);
           console.log(`Buscando Vendor ID para: ${vendorName} (código: ${vendorCode})`);
           
           // Try exact match first
           let { data: vendorData, error: vendorError } = await supabase
            .from('vendors')
            .select('id, name')
            .eq('name', vendorName)
            .maybeSingle();
            
           // If exact match fails, try case-insensitive search
           if (!vendorData && !vendorError) {
             console.log(`Tentativa de busca case-insensitive para: ${vendorName}`);
             const { data: vendorDataIlike, error: vendorErrorIlike } = await supabase
              .from('vendors')
              .select('id, name')
              .ilike('name', `%${vendorName}%`)
              .limit(1)
              .maybeSingle();
             vendorData = vendorDataIlike;
             vendorError = vendorErrorIlike;
           }
           
           // If still not found, try searching by the original vendor code
           if (!vendorData && !vendorError && vendorCode !== vendorName) {
             console.log(`Tentativa de busca pelo código original: ${vendorCode}`);
             const { data: vendorDataCode, error: vendorErrorCode } = await supabase
              .from('vendors')
              .select('id, name')
              .ilike('name', `%${vendorCode}%`)
              .limit(1)
              .maybeSingle();
             vendorData = vendorDataCode;
             vendorError = vendorErrorCode;
           }
          
          if (vendorError) {
            console.error('Erro ao buscar vendor:', vendorError);
          } else if (vendorData) {
             vendorId = vendorData.id;
             console.log(`Vendor ID encontrado: ${vendorId} para "${vendorData.name}"`);
          } else {
            console.warn(`Vendor não encontrado: ${vendorName} (código: ${vendorCode}). Produtos serão importados sem vendor_id.`);
            toast.warning(`Vendor "${vendorName}" não encontrado. Produtos serão importados sem associação de fornecedor.`);
          }
      }

      // Family ID is already available in selectedFamily state
      const selectedFamilyId = selectedFamily;

      // --- Start Processing Loop --- 
      for (let i = 0; i < subscriptionPlansData.length; i++) {
        const planData = subscriptionPlansData[i];
        setCurrentProcessingIndex(i);

        // Update progress message
        setImportProgress(prev => {
             const newProgress = [...prev];
             if (newProgress[i]) {
                 newProgress[i] = { ...newProgress[i], status: 'pending', message: 'Verificando duplicidade...' };
             }
             return newProgress;
         });

        try {
          // Como agora usamos a view formatada, verificamos se já tem external_id
          // Produtos já importados não deveriam estar na seleção, mas mantemos verificação de segurança
          if (planData.external_id) {
            console.log(`Plano ${planData.name} já foi importado anteriormente. Pulando.`);
            duplicatePlansCount++;
            setImportProgress(prev => {
                const newProgress = [...prev];
                // *** Use 'error' status for skipped items, clarify in message ***
                if(newProgress[i]) newProgress[i] = { ...newProgress[i], status: 'error', message: 'Produto já existe (ignorado)' };
                return newProgress;
            });
            processedCount++;
            continue; // Skip to the next product
          }

          setImportProgress(prev => {
            const newProgress = [...prev];
            if(newProgress[i]) newProgress[i].message = 'Inserindo plano...';
            return newProgress;
          });

                     // Prepare data for DB insert (exclude fields not in subscription_plans table)
          const { prices, external_id, vendor, ingram_product_id, mpn_custom, ...planToInsert } = planData;
          
          // Garantir que description da view seja usado
          planToInsert.description = planData.description || '';
          
          // Ensure maximum_quantity is set to a valid number if undefined
          const safeMaxQuantity = planToInsert.maximum_quantity || 9999;
          
          const dbPlanData = {
             ...planToInsert,
             maximum_quantity: safeMaxQuantity,
             vendor_id: vendorId, // Assign fetched vendorId
             family_id: selectedFamilyId // Assign familyId from state
          };

          // 2. Insert into subscription_plans table
          const { data: planInsertData, error: planError } = await supabase
            .from('subscription_plans')
            .insert(dbPlanData)
            .select()
            .single();

          if (planError) {
            if (planError.code === '23505') {
                 console.warn(`Falha ao inserir plano (duplicado?) ${planData.name}:`, planError.message);
                 setImportProgress(prev => {
                   const newProgress = [...prev];
                   if(newProgress[i]) newProgress[i] = { ...newProgress[i], status: 'error', message: 'Erro: Plano duplicado (nome/SKU?)' };
                   return newProgress;
                 });
                 errorCount++;
                 processedCount++;
                 continue;
            } else {
                 console.error(`Erro ao inserir plano ${planData.name}:`, planError);
                 throw planError; // Rethrow other critical errors
            }
          }
          
          const insertedPlanId = planInsertData.id;
          console.log(`Plano ${planData.name} inserido com ID: ${insertedPlanId}`);

          setImportProgress(prev => {
            const newProgress = [...prev];
            if(newProgress[i]) newProgress[i].message = 'Mapeando ID externo...';
            return newProgress;
          });

          // 3. Insert external ID mapping for Ingram Micro
          const { error: externalIdError } = await supabase
            .from('external_ids')
            .insert({
              entity_name: 'subscription_plans',
              entity_id: insertedPlanId,
              system_name: 'ingram_micro',
              external_id: planData.mpn_custom || planData.ingram_product_id // Use mpn_custom or fallback to product ID
            });
          if (externalIdError) {
              console.error('Erro ao salvar ID externo Ingram:', externalIdError);
              toast.warning(`Aviso: Erro ao salvar ID externo Ingram para ${planData.name}`);
              // Continue processing even if this fails for now
          }

          setImportProgress(prev => {
            const newProgress = [...prev];
            if(newProgress[i]) newProgress[i].message = 'Inserindo preços...';
            return newProgress;
          });

          // 4. Insert prices
          let brlPriceAmount: number | null = null;
          let usdPriceAmount: number | null = null;
          let priceInsertionWarning = false;
          try {
             const pricesToInsert = planData.prices.map(priceData => ({
                 subscription_plan_id: insertedPlanId,
                 currency: priceData.currency,
                 price_amount: priceData.price_amount,
                 cost_amount: priceData.cost_amount,
                 margin: priceData.margin,
                 created_by: 'import_process',
                 updated_by: 'import_process'
             }));

             if (pricesToInsert.length > 0) {
                 const { error: pricesError } = await supabase
                     .from('subscription_plan_prices')
                     .insert(pricesToInsert);

                 if (pricesError) {
                     // Attempt individual inserts if batch fails due to unique constraint
                     const { successCount: priceSuccess, errorCount: priceErrorCount } = await handleSupabaseUniqueConstraintError(
                         pricesError,
                         pricesToInsert,
                         'subscription_plan_prices'
                     );

                     if (priceErrorCount > 0) {
                         priceInsertionWarning = true;
                         console.warn(`${priceErrorCount} preços falharam ao inserir para ${planData.name} (duplicados?).`);
                         // Warning will be set later
                     }
                     // Store potentially existing prices even if some failed
                     const insertedOrExistingBrl = planData.prices.find(p => p.currency === 'BRL');
                     if (insertedOrExistingBrl) brlPriceAmount = insertedOrExistingBrl.price_amount;
                     const insertedOrExistingUsd = planData.prices.find(p => isUSD(p.currency));
                     if (insertedOrExistingUsd) usdPriceAmount = insertedOrExistingUsd.price_amount;

                 } else {
                     // Batch insert successful
                     console.log(`Inserido ${pricesToInsert.length} preços para ${planData.name}`);
                     const brlPrice = pricesToInsert.find(p => p.currency === 'BRL');
                     if (brlPrice) brlPriceAmount = brlPrice.price_amount;
                     const usdPrice = pricesToInsert.find(p => isUSD(p.currency));
                     if (usdPrice) usdPriceAmount = usdPrice.price_amount;
                 }
             } else {
                 console.warn(`Nenhum preço encontrado para importar para o plano ${planData.name}`);
             }

             setImportProgress(prev => {
                const newProgress = [...prev];
                if(newProgress[i]) newProgress[i].message = 'Convertendo preços (USD->BRL)...';
                return newProgress;
             });

             // --- BRL Conversion Logic (Attempt after initial inserts) ---
             const usdPriceData = planData.prices.find(p => isUSD(p.currency));
             if (usdPriceData && usdPriceData.price_amount > 0) {
                   const { data: existingBrlPrice, error: checkBrlError } = await supabase
                       .from('subscription_plan_prices')
                       .select('id, price_amount')
                       .eq('subscription_plan_id', insertedPlanId)
                       .eq('currency', 'BRL')
                       .maybeSingle();

                   if (checkBrlError) console.error('Erro ao verificar preço BRL existente:', checkBrlError);

                   if (!existingBrlPrice && !checkBrlError) {
                       const brlPriceAmountConverted = parseFloat((usdPriceData.price_amount * USD_TO_BRL_RATE).toFixed(2));
                       const brlCostAmount = parseFloat((usdPriceData.cost_amount * USD_TO_BRL_RATE).toFixed(2));
                       const brlPriceToInsert = {
                           subscription_plan_id: insertedPlanId,
                           currency: 'BRL',
                           price_amount: brlPriceAmountConverted,
                           cost_amount: brlCostAmount,
                           margin: usdPriceData.margin,
                           created_by: 'import_process',
                           updated_by: 'import_process'
                       };
                       const { error: brlPriceError } = await supabase
                           .from('subscription_plan_prices')
                           .insert(brlPriceToInsert);

                       if (brlPriceError) {
                           console.error(`Erro ao inserir preço BRL convertido para ${planData.name}:`, brlPriceError);
                           toast.error(`Aviso: Erro ao inserir preço BRL para ${planData.name}: ${brlPriceError.message}`);
                       } else {
                           console.log(`Preço BRL convertido inserido para ${planData.name}`);
                           convertedPricesCount++;
                           brlPriceAmount = brlPriceAmountConverted; // Update for HubSpot
                       }
                   } else if (existingBrlPrice) {
                       console.log(`Preço BRL já existe para ${planData.name}, usando valor existente.`);
                       brlPriceAmount = existingBrlPrice.price_amount; // Use existing for HubSpot
                   }
             }
             // Ensure usdPriceAmount is set if original was USD
             if (usdPriceData) usdPriceAmount = usdPriceData.price_amount;

          } catch (priceProcessingError: any) {
             console.error(`Erro CRÍTICO processando preços para ${planData.name}:`, priceProcessingError);
             setImportProgress(prev => {
               const newProgress = [...prev];
               if(newProgress[i]) newProgress[i] = { ...newProgress[i], status: 'error', message: `Erro nos preços: ${priceProcessingError.message || priceProcessingError}` };
               return newProgress;
             });
             errorCount++;
             processedCount++; // Count as processed (with error)
             continue; // Skip HubSpot if prices failed critically
          }

          setImportProgress(prev => {
            const newProgress = [...prev];
            if(newProgress[i]) newProgress[i].message = 'Sincronizando com HubSpot...';
            return newProgress;
          });

          // 5. Create/Update product in HubSpot
          try {
             // Prepare data for HubSpot function (using the inserted plan ID)
             const hubspotPlanData = { ...planData, id: insertedPlanId };
             
             // Encontrar custos correspondentes aos preços
             const brlCost = planData.prices.find(price => price.currency === 'BRL')?.cost_amount;
             const usdCost = planData.prices.find(price => isUSD(price.currency))?.cost_amount;
             
             const hubspotResult = await createProductInHubspot(hubspotPlanData, planData.prices);

             if (hubspotResult.success && hubspotResult.data?.id) {
                 const hubspotProductId = hubspotResult.data.id;
                 console.log(`HubSpot produto criado/atualizado: ${hubspotProductId} para ${planData.name}`);
                 // Save HubSpot external ID mapping
                 const { error: hubspotIdError } = await supabase
                    .from('external_ids')
                    .insert({
                      entity_name: 'subscription_plans',
                      entity_id: insertedPlanId,
                      system_name: 'hubspot',
                      external_id: hubspotProductId
                    });

                 if (hubspotIdError) {
                     console.error('Erro ao salvar ID HubSpot:', hubspotIdError);
                     toast.warning(`Aviso: Erro ao salvar ID HubSpot para ${planData.name}`);
                     // Mark as success with warning
                     setImportProgress(prev => {
                         const newProgress = [...prev];
                         // *** Use 'success' status, retain warning message ***
                         if (newProgress[i] && newProgress[i].status !== 'error') {
                            newProgress[i] = { ...newProgress[i], status: 'success', message: 'Importado (erro ao salvar ID HubSpot)' };
                         }
                         return newProgress;
                     });
                 } else {
                     // Final success step
                     setImportProgress(prev => {
                       const newProgress = [...prev];
                       // *** Update to success only if still pending ***
                       if (newProgress[i] && newProgress[i].status === 'pending') {
                           // Add message based on price warning
                           const message = priceInsertionWarning ? 'Importado com avisos (preços?)' : 'Importado com sucesso';
                           newProgress[i] = { ...newProgress[i], status: 'success', message: message };
                       } else if (newProgress[i] && newProgress[i].status === 'success' && priceInsertionWarning && !newProgress[i].message?.includes('aviso')) {
                           // If already success but had price warning, update message
                            newProgress[i].message = 'Importado com avisos (preços?)';
                       }
                       return newProgress;
                     });
                 }
             } else {
                 // HubSpot function returned an error
                 console.error(`Erro HubSpot para ${planData.name}:`, hubspotResult.message);
                 setImportProgress(prev => {
                     const newProgress = [...prev];
                     if(newProgress[i]) newProgress[i] = { ...newProgress[i], status: 'error', message: `Erro HubSpot: ${hubspotResult.message || 'Desconhecido'}` };
                     return newProgress;
                 });
                 errorCount++;
             }
          } catch (hubspotError: any) {
             // Exception calling HubSpot function
             console.error(`Erro EXCEPTION chamando HubSpot para ${planData.name}:`, hubspotError);
             setImportProgress(prev => {
                 const newProgress = [...prev];
                 if(newProgress[i]) newProgress[i] = { ...newProgress[i], status: 'error', message: `Erro HubSpot: ${hubspotError.message || hubspotError}` };
                 return newProgress;
             });
             errorCount++;
          }

          processedCount++; // Mark as processed (could be success, warning, or error)

        } catch (error: any) {
          // Catchall for errors during this plan's processing
          console.error(`Erro GERAL processando ${planData.name}:`, error);
           setImportProgress(prev => {
             const newProgress = [...prev];
             if(newProgress[i]) newProgress[i] = {
               ...newProgress[i],
               status: 'error',
               message: `Erro: ${error.message || 'Desconhecido'}`
             };
             return newProgress;
           });
           errorCount++;
           processedCount++; // Count as processed (with error)
        }
      } // End of loop

      // --- Final Summary --- 
      const finalSuccessfulImports = importProgress.filter(p => p.status === 'success').length;
      // Errors = total items processed - successful ones - skipped duplicates
      const finalErrorImports = processedCount - finalSuccessfulImports - duplicatePlansCount;
      
      console.log(`Import Summary: Total=${processedCount}, Success=${finalSuccessfulImports}, Errors=${finalErrorImports}, Duplicates Skipped=${duplicatePlansCount}`);

      if (finalSuccessfulImports > 0) {
        toast.success(`${finalSuccessfulImports} produtos importados/atualizados com sucesso!`);
      }
      // Report errors only if there were actual processing errors (not just skipped duplicates)
      if (finalErrorImports > 0) {
          toast.error(`${finalErrorImports} produtos falharam durante a importação (ver detalhes).`);
      }
      if (duplicatePlansCount > 0) {
        toast.info(`${duplicatePlansCount} produtos já existiam no sistema e foram ignorados.`);
      }
      if (convertedPricesCount > 0) {
        toast.success(`${convertedPricesCount} preços em USD foram automaticamente convertidos para BRL.`);
      }

      setImportComplete(true);
      // Reset selection and refresh list
      setProducts(prevProducts => prevProducts.map(product => ({ ...product, selected: false })));
      setSelectedProducts([]); // Clear selected products list state
      setTimeout(() => fetchProducts(currentPage), 2000);

    } catch (error: any) {
      console.error('Erro CRÍTICO GERAL na importação:', error);
      toast.error(`Falha CRÍTICA na importação: ${error.message || 'Erro desconhecido'}`);
      setImportComplete(true); // Mark as complete even on critical error
    } finally {
      setIsImporting(false);
      // Keep progress modal open for user to review
    }
  };

  // Function to create a new subscription plan family
  const handleCreateFamily = async () => {
    if (!newFamilyName.trim()) {
      toast.error('O nome da família é obrigatório');
      return;
    }
    
    setCreatingFamily(true);
    
    try {
      const { data, error } = await supabase
        .from('subscription_plan_families')
        .insert({
          name: newFamilyName.trim(),
          description: newFamilyDescription.trim() || null
        })
        .select('*')
        .single();
        
      if (error) throw error;
      
      // Add the new family to the list and select it
      setPlanFamilies([...planFamilies, data]);
      setSelectedFamily(data.id);
      
      // Reset the form
      setNewFamilyName('');
      setNewFamilyDescription('');
      setShowNewFamilyForm(false);
      
      toast.success('Família de planos criada com sucesso!');
    } catch (error: any) {
      console.error('Erro ao criar família de planos:', error);
      toast.error(`Falha ao criar família de planos: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setCreatingFamily(false);
    }
  };

  // Função para atualizar o item selecionado para mostrar detalhes
  const handleProgressItemClick = (index: number) => {
    if (selectedProgressItem === index) {
      setSelectedProgressItem(null);
    } else {
      setSelectedProgressItem(index);
    }
  };

  // Função para abrir modal de mesclagem
  const handleMergeClick = (product: IngramProduct) => {
    setSelectedProductForMerge(product);
    setHubspotProductId('');
    setSelectedMergeFamily(null);
    setShowMergeModal(true);
  };

  // Função para mesclar produto com HubSpot existente
  const handleMergeProduct = async () => {
    if (!selectedProductForMerge || !hubspotProductId.trim()) {
      toast.error('Por favor, informe o ID do produto no HubSpot');
      return;
    }

    setIsMerging(true);
    
    try {
      // 1. Buscar vendor_id baseado no nome do vendor
      let vendorId: string | null = null;
      if (selectedProductForMerge.vendor) {
        const vendorCode = selectedProductForMerge.vendor;
        const vendorName = getVendorSearchName(vendorCode);
        console.log(`Buscando Vendor ID para: ${vendorName} (código: ${vendorCode})`);
        
        // Tentar busca exata primeiro
        let { data: vendorData, error: vendorError } = await supabase
          .from('vendors')
          .select('id, name')
          .eq('name', vendorName)
          .maybeSingle();
          
        // Se não encontrar, tentar busca case-insensitive
        if (!vendorData && !vendorError) {
          console.log(`Tentativa de busca case-insensitive para: ${vendorName}`);
          const { data: vendorDataIlike, error: vendorErrorIlike } = await supabase
            .from('vendors')
            .select('id, name')
            .ilike('name', `%${vendorName}%`)
            .limit(1)
            .maybeSingle();
          vendorData = vendorDataIlike;
          vendorError = vendorErrorIlike;
        }
        
        // Se ainda não encontrou, tentar pelo código original
        if (!vendorData && !vendorError && vendorCode !== vendorName) {
          console.log(`Tentativa de busca pelo código original: ${vendorCode}`);
          const { data: vendorDataCode, error: vendorErrorCode } = await supabase
            .from('vendors')
            .select('id, name')
            .ilike('name', `%${vendorCode}%`)
            .limit(1)
            .maybeSingle();
          vendorData = vendorDataCode;
          vendorError = vendorErrorCode;
        }
       
        if (vendorError) {
          console.error('Erro ao buscar vendor:', vendorError);
        } else if (vendorData) {
          vendorId = vendorData.id;
          console.log(`Vendor ID encontrado: ${vendorId} para "${vendorData.name}"`);
        } else {
          console.warn(`Vendor não encontrado: ${vendorName} (código: ${vendorCode}). Produto será importado sem vendor_id.`);
          toast.warning(`Vendor "${vendorName}" não encontrado. Produto será importado sem associação de fornecedor.`);
        }
      }

      // 2. Processar os dados do produto para importação
      const planData = processSubscriptionPlan(selectedProductForMerge, selectedMergeFamily, selectedUnitMeasure);
      
      // 3. Inserir na tabela subscription_plans
      const { data: planInsertData, error: planInsertError } = await supabase
        .from('subscription_plans')
        .insert({
          name: planData.name,
          description: planData.description,
          billing_frequency: planData.billing_frequency,
          is_active: planData.is_active,
          sku: planData.sku,
          vendor_id: vendorId, // Usar o vendor_id encontrado ao invés do valor string
          vendor_subscription_name: planData.vendor_subscription_name,
          vendor_part_number: planData.vendor_part_number,
          minimum_quantity: planData.minimum_quantity,
          maximum_quantity: planData.maximum_quantity || 9999,
          billing_model: planData.billing_model,
          pricing_model: planData.pricing_model,
          billing_period_type: planData.billing_period_type,
          billing_period_duration: planData.billing_period_duration,
          subscription_period_type: planData.subscription_period_type,
          subscription_period_duration: planData.subscription_period_duration,
          family_id: planData.family_id,
          unit_measure: planData.unit_measure,
          service_type: 'licensing',
          imported: true,
          created_by: planData.created_by,
          updated_by: planData.updated_by
        })
        .select('id')
        .single();

      if (planInsertError) throw planInsertError;
      
      const plan_id = planInsertData.id;
      
      // 4. Inserir referência do Ingram Micro na tabela external_ids
      const { error: ingramIdError } = await supabase
        .from('external_ids')
        .insert({
          external_id: planData.external_id,
          entity_id: plan_id,
          system_name: planData.system_name,
          entity_name: planData.entity_name
        });

      if (ingramIdError) throw ingramIdError;
      
      // 5. Inserir ID do HubSpot fornecido pelo usuário na tabela external_ids
      const { error: hubspotIdError } = await supabase
        .from('external_ids')
        .insert({
          entity_name: 'subscription_plans',
          entity_id: plan_id,
          system_name: 'hubspot',
          external_id: hubspotProductId.trim()
        });
        
      if (hubspotIdError) throw hubspotIdError;
      
      // 6. Inserir preços na tabela subscription_plan_prices
      let brlPriceAmount: number | null = null;
      let usdPriceAmount: number | null = null;
      let priceInsertionWarning = false;
      let convertedPricesCount = 0;

      try {
        const pricesToInsert = planData.prices.map(priceData => ({
          subscription_plan_id: plan_id,
          currency: priceData.currency,
          price_amount: priceData.price_amount,
          cost_amount: priceData.cost_amount,
          margin: priceData.margin,
          created_by: 'import_process',
          updated_by: 'import_process'
        }));

        if (pricesToInsert.length > 0) {
          const { error: pricesError } = await supabase
            .from('subscription_plan_prices')
            .insert(pricesToInsert);

          if (pricesError) {
            // Attempt individual inserts if batch fails due to unique constraint
            const { successCount: priceSuccess, errorCount: priceErrorCount } = await handleSupabaseUniqueConstraintError(
              pricesError,
              pricesToInsert,
              'subscription_plan_prices'
            );

            if (priceErrorCount > 0) {
              priceInsertionWarning = true;
              console.warn(`${priceErrorCount} preços falharam ao inserir para ${planData.name} (duplicados?).`);
            }
            // Store potentially existing prices even if some failed
            const insertedOrExistingBrl = planData.prices.find(p => p.currency === 'BRL');
            if (insertedOrExistingBrl) brlPriceAmount = insertedOrExistingBrl.price_amount;
            const insertedOrExistingUsd = planData.prices.find(p => isUSD(p.currency));
            if (insertedOrExistingUsd) usdPriceAmount = insertedOrExistingUsd.price_amount;

          } else {
            // Batch insert successful
            console.log(`Inserido ${pricesToInsert.length} preços para ${planData.name}`);
            const brlPrice = pricesToInsert.find(p => p.currency === 'BRL');
            if (brlPrice) brlPriceAmount = brlPrice.price_amount;
            const usdPrice = pricesToInsert.find(p => isUSD(p.currency));
            if (usdPrice) usdPriceAmount = usdPrice.price_amount;
          }
        } else {
          console.warn(`Nenhum preço encontrado para importar para o plano ${planData.name}`);
        }

        // --- BRL Conversion Logic (Attempt after initial inserts) ---
        const usdPriceData = planData.prices.find(p => isUSD(p.currency));
        if (usdPriceData && usdPriceData.price_amount > 0) {
          const { data: existingBrlPrice, error: checkBrlError } = await supabase
            .from('subscription_plan_prices')
            .select('id, price_amount')
            .eq('subscription_plan_id', plan_id)
            .eq('currency', 'BRL')
            .maybeSingle();

          if (checkBrlError) console.error('Erro ao verificar preço BRL existente:', checkBrlError);

          if (!existingBrlPrice && !checkBrlError) {
            const brlPriceAmountConverted = parseFloat((usdPriceData.price_amount * USD_TO_BRL_RATE).toFixed(2));
            const brlCostAmount = parseFloat((usdPriceData.cost_amount * USD_TO_BRL_RATE).toFixed(2));
            const brlPriceToInsert = {
              subscription_plan_id: plan_id,
              currency: 'BRL',
              price_amount: brlPriceAmountConverted,
              cost_amount: brlCostAmount,
              margin: usdPriceData.margin,
              created_by: 'import_process',
              updated_by: 'import_process'
            };
            const { error: brlPriceError } = await supabase
              .from('subscription_plan_prices')
              .insert(brlPriceToInsert);

            if (brlPriceError) {
              console.error(`Erro ao inserir preço BRL convertido para ${planData.name}:`, brlPriceError);
              toast.warning(`Aviso: Erro ao inserir preço BRL para ${planData.name}: ${brlPriceError.message}`);
            } else {
              console.log(`Preço BRL convertido inserido para ${planData.name}`);
              convertedPricesCount++;
              brlPriceAmount = brlPriceAmountConverted; // Update for HubSpot
            }
          } else if (existingBrlPrice) {
            console.log(`Preço BRL já existe para ${planData.name}, usando valor existente.`);
            brlPriceAmount = existingBrlPrice.price_amount; // Use existing for HubSpot
          }
        }
        // Ensure usdPriceAmount is set if original was USD
        if (usdPriceData) usdPriceAmount = usdPriceData.price_amount;

      } catch (priceProcessingError: any) {
        console.error(`Erro CRÍTICO processando preços para ${planData.name}:`, priceProcessingError);
        throw new Error(`Erro nos preços: ${priceProcessingError.message || priceProcessingError}`);
      }
      
      // 7. Buscar dados da view update_hubspot_product e chamar edge function
      const { data: hubspotViewData, error: hubspotViewError } = await supabase
        .from('update_hubspot_product')
        .select('*')
        .eq('id', plan_id)
        .single();

      if (hubspotViewError || !hubspotViewData) {
        console.error('Erro ao buscar dados da view update_hubspot_product:', hubspotViewError);
        throw new Error('Não foi possível obter dados para sincronização com HubSpot');
      }

      console.log('Dados da view update_hubspot_product:', hubspotViewData);

      // Mapear o tipo de produto para os valores aceitos pelo HubSpot
      // IMPORTANTE: Esta função deve ser IDÊNTICA às outras implementações
      const getHubSpotProductType = (serviceType: string): string => {
        console.log('🔍 Mapeando service_type:', serviceType, 'tipo:', typeof serviceType);
        
        // Normalizar o valor (trim e lowercase)
        const normalizedType = serviceType?.trim()?.toLowerCase();
        console.log('🔍 Valor normalizado:', normalizedType);
        
        if (normalizedType === 'licensing') {
          console.log('✅ Mapeado para license');
          return 'license';
        }
        if (normalizedType === 'material') {
          console.log('✅ Mapeado para material');
          return 'material';
        }
        if (normalizedType === 'program') {
          console.log('✅ Mapeado para program');
          return 'program';
        }
        if (normalizedType === 'professional_service') {
          console.log('✅ Mapeado para service');
          return 'service';
        }
        console.log('✅ Mapeado para service (default)');
        return 'service'; // Default para todos os outros casos
      };

      // Construir o payload usando os dados da view (seguindo padrão do hubspot-direct-sync.ts)
      const hubspotPayload = {
        productId: hubspotViewData.productid, // campo vem da view como productid
        name: hubspotViewData.name || '',
        description: hubspotViewData.description || '',
        hs_sku: hubspotViewData.hs_sku || '', // Adicionando o campo hs_sku da view
        hs_price_brl: hubspotViewData.hs_price_brl?.toString() || '0.00',
        hs_price_usd: hubspotViewData.hs_price_usd?.toString() || '0.00',
        hs_product_type: getHubSpotProductType(hubspotViewData.hs_product_type),
        recurringbillingfrequency: normalizeBillingFrequency(hubspotViewData.recurringbillingfrequency || 'month', false)
      };

      console.log('Payload para edge function:', hubspotPayload);

      // Fazer as requisições OPTIONS e POST como no hubspot-direct-sync.ts
      const edgeFunctionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-product-hubspot`;
      const authToken = `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`;

      // Primeiro, fazer a requisição OPTIONS
      console.log('Fazendo requisição OPTIONS...');
      const optionsResponse = await fetch(edgeFunctionUrl, {
        method: 'OPTIONS',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      });

      if (!optionsResponse.ok) {
        console.error('Erro na requisição OPTIONS:', optionsResponse.status, optionsResponse.statusText);
        throw new Error('Erro na requisição OPTIONS para edge function');
      }

      console.log('Requisição OPTIONS bem-sucedida');

      // Agora fazer a requisição POST
      console.log('Fazendo requisição POST para sincronização...');
      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(hubspotPayload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro na resposta da edge function:', response.status, errorText);
        
        // Se houver erro, excluir o registro da tabela external_ids
        const { error: deleteError } = await supabase
          .from('external_ids')
          .delete()
          .eq('entity_id', selectedProductForMerge.id)
          .eq('system_name', 'hubspot');
          
        if (deleteError) {
          console.error('Erro ao excluir registro de external_ids:', deleteError);
          // Não lançar erro aqui para não mascarar o erro original do HubSpot
        } else {
          console.log('Registro de external_ids excluído com sucesso após erro no HubSpot');
        }
        
        throw new Error(`Erro da edge function: ${response.status} - ${errorText}`);
      }

      const responseData = await response.json();
      console.log('Resposta da edge function:', responseData);

      if (responseData.success !== false) {
        let successMessage = `Produto ${planData.name} mesclado com sucesso!`;
        if (convertedPricesCount > 0) {
          successMessage += ` ${convertedPricesCount} preço(s) em USD convertido(s) para BRL.`;
        }
        if (priceInsertionWarning) {
          successMessage += ' (alguns preços podem ter tido avisos)';
        }
        
        toast.success(successMessage);
        setShowMergeModal(false);
        setSelectedProductForMerge(null);
        setHubspotProductId('');
        setSelectedMergeFamily(null);
        
        // Atualizar a lista de produtos
        fetchProducts(currentPage);
      } else {
        throw new Error(responseData.details || responseData.error || 'Erro ao atualizar produto no HubSpot');
      }
      
    } catch (error: any) {
      console.error('Erro ao mesclar produto:', error);
      toast.error(`Erro ao mesclar produto: ${error.message}`);
    } finally {
      setIsMerging(false);
    }
  };

  // Monitorar mudanças na lista de produtos selecionados
  useEffect(() => {
    console.log(`Número de produtos selecionados: ${selectedProducts.length}`);
  }, [selectedProducts]);

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Page Header and Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Importar Planos de Assinatura</h1>
          <p className="text-muted-foreground">
            Importe planos de assinatura do catálogo Ingram Cloud Marketplace
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/subscription-plans')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <Button
            onClick={handleImportClick}
            disabled={selectedProducts.filter(p => !p.isImported).length === 0}
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Importar Selecionados ({selectedProducts.filter(p => !p.isImported).length})
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtrar Produtos</CardTitle>
          <CardDescription>
            Filtre os produtos por fornecedor, nome, número de série (MPN) ou status de importação
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="flex-1">
              <Label htmlFor="vendor">Fornecedor</Label>
              <Select value={vendorFilter} onValueChange={setVendorFilter}>
                <SelectTrigger id="vendor">
                  <SelectValue placeholder="Todos os fornecedores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os fornecedores</SelectItem>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label htmlFor="name">Nome do Produto</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="name"
                  placeholder="Buscar por nome"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="flex-1">
              <Label htmlFor="mpn">MPN</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="mpn"
                  placeholder="Buscar por MPN"
                  value={mpnFilter}
                  onChange={(e) => setMpnFilter(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="flex-1">
              <Label htmlFor="imported">Status</Label>
              <Select value={importedFilter} onValueChange={setImportedFilter}>
                <SelectTrigger id="imported">
                  <SelectValue placeholder="Todos os planos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os planos</SelectItem>
                  <SelectItem value="not_imported">Não importados</SelectItem>
                  <SelectItem value="imported">Importados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClearFilters}>
              <X className="h-4 w-4 mr-2" />
              Limpar Filtros
            </Button>
            <Button onClick={handleApplyFilters}>
              <Search className="h-4 w-4 mr-2" />
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Produtos Disponíveis</CardTitle>
          <CardDescription>
            Selecione os produtos que deseja importar para o catálogo
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[30px]">
                    <Checkbox
                      checked={products.length > 0 && products.filter(p => !p.isImported).every(p => p.selected)}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead>Período de Cobrança</TableHead>
                  <TableHead>Período de Assinatura</TableHead>
                  <TableHead>Preço</TableHead>
                  <TableHead className="w-[50px] text-center">Importado</TableHead>
                  <TableHead className="w-[50px] text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex flex-col items-center">
                        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mb-2" />
                        <p className="text-muted-foreground">Carregando produtos...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      Nenhum produto encontrado. Tente ajustar os filtros.
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product, index) => (
                    <TableRow 
                      key={product.uniqueId}
                      className={`cursor-pointer ${product.isImported ? 'opacity-60 bg-gray-50' : ''}`}
                      onClick={() => !product.isImported && handleRowClick(product.uniqueId)}
                    >
                      <TableCell onClick={(e) => {
                        e.stopPropagation();
                        if (!product.isImported) {
                          handleSelectProduct(product.uniqueId, !product.selected);
                        }
                      }}>
                        <Checkbox 
                          checked={product.selected}
                          disabled={product.isImported}
                          onCheckedChange={(checked) => {
                            if (!product.isImported) {
                              handleSelectProduct(product.uniqueId, checked as boolean);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-extrabold">
                        <div>
                          <div>{product.name}</div>
                          <div className="text-xs text-muted-foreground mt-1">{product.mpn}</div>
                        </div>
                      </TableCell>
                      <TableCell>{product.vendor}</TableCell>
                      <TableCell>{formatPeriodWithDuration(product.billingPeriod.type, product.billingPeriod.duration)}</TableCell>
                      <TableCell>{formatPeriodWithDuration(product.subscriptionPeriod.type, product.subscriptionPeriod.duration)}</TableCell>
                      <TableCell>{getFormattedPrice(product)}</TableCell>
                      <TableCell className="p-0">
                        <div className="flex items-center justify-center h-[52px]">
                          {product.isImported ? (
                            <Badge variant="secondary" className="bg-green-100 text-green-800 w-8 h-8 flex items-center justify-center p-0">
                              <Check className="h-4 w-4" />
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="w-8 h-8 flex items-center justify-center p-0">
                              <X className="h-4 w-4" />
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="p-0">
                        <div className="flex items-center justify-center h-[52px]">
                          {!product.isImported && (
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMergeClick(product);
                              }}
                              title="Mesclar"
                              className="w-8 h-8"
                            >
                              <Merge className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination */}
          {!isLoading && products.length > 0 && renderPagination()}
        </CardContent>
      </Card>

      {/* Family Selection Dialog */}
      <Dialog open={showFamilyModal} onOpenChange={setShowFamilyModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Selecionar Família de Planos</DialogTitle>
            <DialogDescription>
              Escolha a família para os {selectedProducts.length} planos selecionados ou crie uma nova família.
            </DialogDescription>
          </DialogHeader>
          
          {!showNewFamilyForm ? (
            <>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="family">Família de Planos</Label>
                  <Select value={selectedFamily || ''} onValueChange={setSelectedFamily}>
                    <SelectTrigger id="family">
                      <SelectValue placeholder="Selecione uma família" />
                    </SelectTrigger>
                    <SelectContent>
                      {planFamilies.map((family) => (
                        <SelectItem key={family.id} value={family.id}>
                          {family.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button 
                    variant="link"
                    onClick={() => setShowNewFamilyForm(true)}
                    className="p-0 h-auto mt-2"
                  >
                    <PlusCircle className="h-4 w-4 mr-2" />
                    Criar nova família
                  </Button>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowFamilyModal(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleImportWithFamily}
                  disabled={!selectedFamily}
                >
                  Importar Planos
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="familyName">Nome da Família</Label>
                  <Input
                    id="familyName"
                    value={newFamilyName}
                    onChange={(e) => setNewFamilyName(e.target.value)}
                    placeholder="ex: Microsoft 365"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="familyDescription">Descrição</Label>
                  <Textarea
                    id="familyDescription"
                    value={newFamilyDescription}
                    onChange={(e) => setNewFamilyDescription(e.target.value)}
                    placeholder="Descrição opcional para a família de planos"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewFamilyForm(false)}>
                  Voltar
                </Button>
                <Button 
                  onClick={handleCreateFamily}
                  disabled={!newFamilyName || creatingFamily}
                >
                  {creatingFamily ? 'Criando...' : 'Criar Família'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={showMergeModal} onOpenChange={setShowMergeModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Mesclar com Produto HubSpot</DialogTitle>
            <DialogDescription>
              O produto "{selectedProductForMerge?.name}" será importado e mesclado com um produto existente no HubSpot.
              Por favor, informe o ID do produto no HubSpot.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="merge-family">Família de Planos</Label>
              <Select value={selectedMergeFamily || ''} onValueChange={setSelectedMergeFamily}>
                <SelectTrigger id="merge-family">
                  <SelectValue placeholder="Selecione uma família (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  {planFamilies.map((family) => (
                    <SelectItem key={family.id} value={family.id}>
                      {family.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="hubspot-id">ID do Produto no HubSpot</Label>
              <Input
                id="hubspot-id"
                value={hubspotProductId}
                onChange={(e) => setHubspotProductId(e.target.value)}
                placeholder="Digite o ID do produto no HubSpot"
                disabled={isMerging}
              />
              <p className="text-sm text-muted-foreground">
                Este ID será usado para conectar o plano importado ao produto existente no HubSpot.
              </p>
            </div>
            
            {selectedProductForMerge && (
              <div className="bg-muted/40 rounded-lg p-3">
                <h4 className="font-extrabold mb-2">Detalhes do Produto:</h4>
                <div className="text-sm space-y-1">
                  <p><strong>Nome:</strong> {selectedProductForMerge.name}</p>
                  <p><strong>MPN:</strong> {selectedProductForMerge.mpn}</p>
                  <p><strong>Fornecedor:</strong> {selectedProductForMerge.vendor}</p>
                  <p><strong>Preço:</strong> {getFormattedPrice(selectedProductForMerge)}</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowMergeModal(false)}
              disabled={isMerging}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleMergeProduct}
              disabled={!hubspotProductId.trim() || isMerging}
            >
              {isMerging ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Mesclando...
                </>
              ) : (
                <>
                  <Merge className="h-4 w-4 mr-2" />
                  Mesclar Produto
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress Dialog */}
      <Dialog open={showProgressModal} onOpenChange={(open) => {
        // Only allow closing if import is complete
        if (importComplete) setShowProgressModal(open);
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Importação em Andamento</DialogTitle>
            <DialogDescription>
              {importComplete 
                ? `Importação concluída. ${importProgress.filter(p => p.status === 'success').length} de ${importProgress.length} planos importados com sucesso.`
                : `Importando plano ${currentProcessingIndex + 1} de ${importProgress.length}`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[350px] overflow-y-auto">
            {importProgress.map((item, index) => (
              <div 
                key={index} 
                className={cn(
                  "py-2 px-3 mb-2 rounded-md border flex items-center justify-between cursor-pointer",
                  item.status === 'success' ? 'bg-green-50 border-green-200' : 
                  item.status === 'error' ? 'bg-red-50 border-red-200' : 
                  'bg-yellow-50 border-yellow-200'
                )}
                onClick={() => handleProgressItemClick(index)}
              >
                <div className="flex flex-col">
                  <span className="font-extrabold">{item.name}</span>
                  <span className="text-xs text-muted-foreground">SKU: {item.sku}</span>
                  
                  {selectedProgressItem === index && item.status === 'error' && item.message && (
                    <div className="mt-2 text-xs text-red-700 bg-red-50 p-2 rounded-md border border-red-200">
                      {item.message}
                    </div>
                  )}
                </div>
                <div>
                  {item.status === 'success' && <Check className="h-5 w-5 text-green-600" />}
                  {item.status === 'error' && <X className="h-5 w-5 text-red-600" />}
                  {item.status === 'pending' && <RefreshCw className="h-5 w-5 text-yellow-600 animate-spin" />}
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            {importComplete && (
              <Button onClick={() => {
                setShowProgressModal(false);
                setImportComplete(false);
                setImportProgress([]);
                fetchProducts(); // Recarrega a lista de produtos
              }}>
                Concluir
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImportSubscriptionsPlans; 